import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {CSSProperties} from 'react';
import {AWARD_CARDS, type AwardCard} from '@/constants/badges';
import {colors, transition} from '@/styles/tokens';
import Container from '@/components/atoms/Container';
import Icon from '@/components/atoms/Icon';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './CertificationsSection.module.scss';

const GAP = 20;
const AUTOPLAY_MS = 1600;
const RESET_MS = 320;

// Matches the `.card` flex breakpoints in the module, so the JS-measured "how many cards
// are on screen" (used for the translateX step and the coverflow centre) always
// agrees with what the CSS is actually rendering.
const getVisibleCount = (width: number) => {
    if (width < 600) return 1;
    if (width < 900) return 3;
    return 5;
};

// Dimming of the cards either side of the centre: [neighbour, outer]. On the navy band they fade into the background;
// on the pale `light` ground the same values wash the badges out, so they stay more visible there.
const SIDE_OPACITY = {dark: [0.55, 0.3], light: [0.85, 0.65]} as const;

// The focused (centre) card is scaled up at full opacity; every other card sits
// smaller and dimmed. `scaleUp` is off on phones (only one card is visible there).
const getCoverflowStyle = (distance: number, scaleUp: boolean, light: boolean): CSSProperties => {
    const [neighbourOpacity, outerOpacity] = SIDE_OPACITY[light ? 'light' : 'dark'];
    const abs = Math.abs(distance);
    if (abs === 0) {
        return {
            transform: scaleUp ? 'scale(1.15)' : 'scale(1)',
            zIndex: 2,
            opacity: 1,
            background: colors.background.default,
            boxShadow: 'none'
        };
    }
    if (abs === 1) {
        return {transform: 'scale(0.92)', zIndex: 1, opacity: neighbourOpacity, background: colors.background.default, boxShadow: 'none'};
    }
    return {transform: 'scale(0.78)', zIndex: 0, opacity: outerOpacity, background: colors.background.default, boxShadow: 'none'};
};

const ARROW_ICON_STYLE: CSSProperties = {fontSize: '1rem'};

// Loop copies are decorative: hidden from assistive tech and unfocusable (`inert`; React 18
// has no typed prop for it, so it goes through as a plain attribute).
const cloneAttrs = {'aria-hidden': true, inert: ''} as Record<string, unknown>;

const DEFAULT_SUBHEADING = 'Independent recognition across Web3, blockchain, AI, smart contracts, tokenization, and software development.';

interface CertificationsSectionProps {
    /** Page-specific badge set. Defaults to the full list; at least five keep the carousel full. */
    cards?: AwardCard[];
    subheading?: string;
    /** On a pale section: no navy frame, dark text, white arrows, and only the progress bar under the track. */
    light?: boolean;
}

const CertificationsSection = ({cards = AWARD_CARDS, subheading = DEFAULT_SUBHEADING, light = false}: CertificationsSectionProps) => {
    const total = cards.length;
    const tripled = useMemo(() => [...cards, ...cards, ...cards], [cards]);
    const [index, setIndex] = useState(total);
    const [animated, setAnimated] = useState(true);
    const [paused, setPaused] = useState(false);
    // True after an arrow click, until autoplay takes the next step.
    const [manual, setManual] = useState(false);
    // Bumped on every arrow click to restart the autoplay timer.
    const [navNonce, setNavNonce] = useState(0);
    const resetting = useRef(false);
    const [visible, setVisible] = useState(5);
    const sectionRef = useRef<HTMLDivElement>(null);
    // Paused while off-screen (the page wraps this in `content-visibility: auto`).
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const measure = () => setVisible(getVisibleCount(globalThis.innerWidth));
        measure();
        globalThis.addEventListener('resize', measure);
        return () => globalThis.removeEventListener('resize', measure);
    }, []);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {threshold: 0.1});
        io.observe(node);
        return () => io.disconnect();
    }, []);

    const centerOffset = Math.floor(visible / 2);
    const stepPercent = 100 / visible;
    const logicalIndex = (((index + centerOffset - total) % total) + total) % total;
    const focused = cards[logicalIndex];
    const cycleStep = (((index - total) % total) + total) % total;
    const isWrapping = index >= total * 2;
    const isRestarting = !animated && cycleStep === 0;
    const progressPercent = isWrapping ? 100 : isRestarting ? 0 : ((cycleStep + 1) / total) * 100;

    const next = useCallback(() => {
        if (resetting.current) return;
        setIndex((i) => i + 1);
    }, []);
    const prev = useCallback(() => {
        if (resetting.current) return;
        setIndex((i) => i - 1);
    }, []);
    // Silent reset once the index drifts into a duplicate copy, so the loop never visibly jumps.
    useEffect(() => {
        let t: ReturnType<typeof setTimeout> | undefined;
        if (index >= total * 2) {
            resetting.current = true;
            t = setTimeout(() => {
                setAnimated(false);
                setIndex(index - total);
            }, RESET_MS + 20);
        } else if (index < total) {
            resetting.current = true;
            t = setTimeout(() => {
                setAnimated(false);
                setIndex(index + total);
            }, RESET_MS + 20);
        }
        return () => {
            if (t) clearTimeout(t);
        };
    }, [index, total]);

    useEffect(() => {
        if (!animated) {
            let r2: number;
            const r1 = requestAnimationFrame(() => {
                r2 = requestAnimationFrame(() => {
                    setAnimated(true);
                    resetting.current = false;
                });
            });
            return () => {
                cancelAnimationFrame(r1);
                if (r2) cancelAnimationFrame(r2);
            };
        }
        return undefined;
    }, [animated]);

    useEffect(() => {
        if (paused || !inView) return undefined;
        const timer = setInterval(() => {
            setManual(false);
            next();
        }, AUTOPLAY_MS);
        return () => clearInterval(timer);
    }, [paused, inView, next, navNonce]);

    const handleNext = () => {
        setManual(true);
        setNavNonce((n) => n + 1);
        next();
    };
    const handlePrev = () => {
        setManual(true);
        setNavNonce((n) => n + 1);
        prev();
    };

    // Each badge is exposed to assistive tech once: through its on-screen card if it has one, otherwise
    // through the middle copy. Cards on screen (plus one either side, mid-slide) are never hidden.
    const isHiddenCopy = (i: number) => {
        const inViewport = (j: number) => j >= index - 1 && j <= index + visible;
        if (inViewport(i)) return false;
        const item = i % total;
        const onScreenElsewhere = [item, item + total, item + total * 2].some((j) => j !== i && inViewport(j));
        return onScreenElsewhere || i < total || i >= total * 2;
    };

    const trackStyle: CSSProperties = {
        transform: `translateX(calc(-${index} * (${stepPercent}% + ${GAP}px / ${visible})))`,
        transition: animated ? `transform ${transition.slow}` : 'none'
    };
    const progressFillStyle: CSSProperties = {
        width: `${progressPercent}%`,
        transition: animated ? `width ${manual ? RESET_MS : AUTOPLAY_MS}ms linear` : 'none'
    };

    return (
        <div ref={sectionRef} className={light ? `${styles.section} ${styles.light}` : styles.section}>
            <Container>
                <div className={styles.frame}>
                    <h2 className={`font-headers ${styles.heading}`}>
                        Badges &amp; <span className={styles.headingAccent}>Recognition</span>
                    </h2>
                    <p className={`font-regular-text ${styles.subheading}`}>{subheading}</p>

                    <div className={styles.trackRow} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                        <div className={styles.arrowBtn} onClick={handlePrev} role="button" aria-label="Previous">
                            <Icon name="ArrowBackIosNew" style={ARROW_ICON_STYLE} />
                        </div>

                        <div className={styles.trackViewport}>
                            <div className={styles.track} style={trackStyle}>
                                {tripled.map((item, i) => (
                                    <div
                                        key={`${item.name}-${i}`}
                                        {...(isHiddenCopy(i) ? cloneAttrs : {})}
                                        className={styles.card}
                                        style={getCoverflowStyle(i - index - centerOffset, visible > 1, light)}
                                    >
                                        <div className={styles.imageFrame}>
                                            <div className={styles.yearPill}>{item.year}</div>
                                            <OptimizedImage
                                                src={item.src}
                                                alt={item.alt}
                                                width={160}
                                                height={160}
                                                quality={100}
                                                style={{objectFit: 'contain', maxWidth: '100%', maxHeight: '100%'}}
                                            />
                                        </div>
                                        <div className={styles.divider} />
                                        <div className={styles.cardBody}>
                                            <p className={`font-headers ${styles.cardTitle}`}>{item.name}</p>
                                            <p className={`font-regular-text ${styles.cardCategory}`}>{item.category}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.arrowBtn} onClick={handleNext} role="button" aria-label="Next">
                            <Icon name="ArrowForwardIos" style={ARROW_ICON_STYLE} />
                        </div>
                    </div>

                    {!light && <p className={`font-headers ${styles.focusedName}`}>{focused.category}</p>}

                    <div className={styles.progressTrack}>
                        <div className={styles.progressFill} style={progressFillStyle} />
                    </div>

                    {/* One string, so React keeps one text node: as separate nodes, the later parts moved
                        each time an earlier one changed length, and Chrome counted that as layout shift. */}
                    <p className={styles.caption}>{`${focused.name} · ${focused.category} · ${focused.region} · ${focused.year}`}</p>
                </div>
            </Container>
        </div>
    );
};

export default memo(CertificationsSection);
